import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import Loader from '../../components/common/Loader';
import Pagination from '../../components/common/Pagination';
import { PERMISSIONS, canFeatureForRole } from '../../utils/permissions';
import { useRolePermissionMatrix } from '../../hooks/useRolePermissionMatrix';
import { getDefaultItemsPerPageSync, getDefaultItemsPerPage } from '../../utils/settings';
import '../../styles/Forms.css';

const CheckinPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(getDefaultItemsPerPageSync());
  const [totalAssets, setTotalAssets] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const searchInputRef = useRef(null);
  const searchTimerRef = useRef(null);

  const { rolePermissions, permissionsReady } = useRolePermissionMatrix();
  const normalizedUserRole = useMemo(() => {
    try {
      const u = JSON.parse(sessionStorage.getItem('user') || '{}');
      return String(u?.role || '').trim().toLowerCase();
    } catch (error) {
      return '';
    }
  }, []);
  const allowCheckinPage = useMemo(
    () =>
      canFeatureForRole(normalizedUserRole, rolePermissions, [PERMISSIONS.CHECKIN]) &&
      canFeatureForRole(normalizedUserRole, rolePermissions, [
        PERMISSIONS.VIEW_ALL_ASSETS,
        PERMISSIONS.VIEW_ASSIGNED_ASSETS,
      ]),
    [normalizedUserRole, rolePermissions]
  );

  useEffect(() => {
    if (!permissionsReady) return;
    if (!allowCheckinPage) {
      toast.error('You do not have permission to use check-in');
      navigate('/admin/dashboard', { replace: true });
    }
  }, [permissionsReady, allowCheckinPage, navigate]);

  // Fetch settings on mount and update itemsPerPage if needed
  useEffect(() => {
    const updateItemsPerPage = async () => {
      const defaultItems = await getDefaultItemsPerPage();
      if (defaultItems !== itemsPerPage) {
        setItemsPerPage(defaultItems);
      }
    };
    updateItemsPerPage();
  }, []);

  useEffect(() => {
    if (!permissionsReady || !allowCheckinPage) return;
    fetchAvailableAssets();
  }, [currentPage, itemsPerPage, searchTerm, permissionsReady, allowCheckinPage]);

  const fetchAvailableAssets = async () => {
    try {
      // Fetch assets with issue_reported and received status
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };

      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      // Fetch both statuses separately and combine
      const [issueRes, receivedRes] = await Promise.all([
        api.getAssets({ ...params, status: 'issue_reported' }),
        api.getAssets({ ...params, status: 'received' })
      ]);

      let allAssets = [];
      let totalCount = 0;

      if (issueRes.success) {
        allAssets.push(...(issueRes.data || []));
        totalCount += issueRes.total || 0;
      }

      if (receivedRes.success) {
        allAssets.push(...(receivedRes.data || []));
        totalCount += receivedRes.total || 0;
      }

      // Remove duplicates (in case an asset appears in both)
      const uniqueAssets = allAssets.filter((asset, index, self) =>
        index === self.findIndex(a => (a.id || a._id) === (asset.id || asset._id))
      );

      setAvailableAssets(uniqueAssets);
      setTotalAssets(totalCount);
      setTotalPages(Math.ceil(totalCount / itemsPerPage) || 1);
    } catch (error) {
      console.error('Error fetching available assets:', error);
      toast.error('Error fetching assets');
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearchTerm(value);
      setCurrentPage(1);
    }, 500);
  };

  const handleToggleAsset = (asset) => {
    const assetId = asset.id || asset._id;
    const isSelected = selectedAssets.some(a => (a.id || a._id) === assetId);
    
    if (isSelected) {
      setSelectedAssets(selectedAssets.filter(a => (a.id || a._id) !== assetId));
    } else {
      setSelectedAssets([...selectedAssets, asset]);
    }
  };

  const handleSelectAll = () => {
    const allSelected = availableAssets.every(asset => 
      selectedAssets.some(sel => (sel.id || sel._id) === (asset.id || asset._id))
    );
    
    if (allSelected) {
      // Deselect all current page assets
      const currentPageIds = availableAssets.map(a => a.id || a._id);
      setSelectedAssets(selectedAssets.filter(sel => 
        !currentPageIds.includes(sel.id || sel._id)
      ));
    } else {
      // Select all current page assets (avoid duplicates)
      const currentPageIds = selectedAssets.map(a => a.id || a._id);
      const newAssets = availableAssets.filter(asset => 
        !currentPageIds.includes(asset.id || asset._id)
      );
      setSelectedAssets([...selectedAssets, ...newAssets]);
    }
  };

  if (!permissionsReady) {
    return <Loader />;
  }
  if (!allowCheckinPage) {
    return null;
  }

  if (loading && availableAssets.length === 0 && selectedAssets.length === 0) {
    return <Loader />;
  }

  const allCurrentPageSelected = availableAssets.length > 0 && 
    availableAssets.every(asset => 
      selectedAssets.some(sel => (sel.id || sel._id) === (asset.id || asset._id))
    );

  return (
    <div className="checkin-page" style={{ padding: '16px' }}>
      {/* Available Assets Section */}
      <div style={{
        background: 'white',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Available Assets</h2>
        
        {/* Search Bar */}
        <div style={{ marginBottom: '16px' }}>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="🔍 Search by tag, description, or category..."
            defaultValue={searchTerm}
            onChange={handleSearchChange}
            autoComplete="off"
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              transition: 'border-color 0.2s',
              outline: 'none'
            }}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
        </div>

        {/* Assets Table */}
        {availableAssets.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📥</div>
            <div style={{ color: '#a0aec0', fontSize: '14px', fontWeight: 500 }}>
              {searchTerm ? 'No assets found matching your search' : 'No assets available for check-in'}
            </div>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)' }}>
                  <tr>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px', width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={allCurrentPageSelected && availableAssets.length > 0}
                        onChange={handleSelectAll}
                        disabled={availableAssets.length === 0}
                        style={{ width: '16px', height: '16px', cursor: availableAssets.length === 0 ? 'not-allowed' : 'pointer', accentColor: '#667eea' }}
                      />
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tag ID</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Description</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Category</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Brand</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Model</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Serial Number</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Assigned To</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Site</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {availableAssets.map((asset) => {
                    const assetId = asset.id || asset._id;
                    const isSelected = selectedAssets.some(a => (a.id || a._id) === assetId);
                    const statusColor = asset.status === 'issue_reported' 
                      ? { bg: '#fed7d7', text: '#c53030' }
                      : { bg: '#bee3f8', text: '#2c5282' };
                    return (
                      <tr
                        key={assetId}
                        onClick={() => handleToggleAsset(asset)}
                        style={{
                          cursor: 'pointer',
                          background: isSelected ? '#edf2f7' : 'white',
                          borderBottom: '1px solid #e2e8f0',
                          transition: 'background 0.15s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) e.currentTarget.style.background = '#f7fafc';
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) e.currentTarget.style.background = 'white';
                        }}
                      >
                        <td style={{ padding: '12px' }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleAsset(asset)}
                            onClick={(e) => e.stopPropagation()}
                            style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#667eea' }}
                          />
                        </td>
                        <td style={{ padding: '12px', fontWeight: 600, color: '#2d3748', fontSize: '13px' }}>
                          {asset.assetTag || 'N/A'}
                        </td>
                        <td style={{ padding: '12px', color: '#4a5568', fontSize: '13px' }}>
                          {asset.description || 'N/A'}
                        </td>
                        <td style={{ padding: '12px', color: '#4a5568', fontSize: '13px' }}>
                          {asset.category || 'N/A'}
                        </td>
                        <td style={{ padding: '12px', color: '#4a5568', fontSize: '13px' }}>
                          {asset.brand || 'N/A'}
                        </td>
                        <td style={{ padding: '12px', color: '#4a5568', fontSize: '13px' }}>
                          {asset.model || 'N/A'}
                        </td>
                        <td style={{ padding: '12px', color: '#4a5568', fontSize: '13px' }}>
                          {asset.serialNumber || 'N/A'}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 600,
                            display: 'inline-block',
                            background: statusColor.bg,
                            color: statusColor.text
                          }}>
                            {asset.status 
                              ? asset.status.charAt(0).toUpperCase() + asset.status.slice(1).replace(/_/g, ' ')
                              : 'N/A'}
                          </span>
                        </td>
                        <td style={{ padding: '12px', color: '#4a5568', fontSize: '13px' }}>
                          {asset.assignedTo?.name 
                            ? `${asset.assignedTo.name}${asset.assignedTo.email ? ` (${asset.assignedTo.email})` : ''}`
                            : asset.location?.site?.name || 'N/A'}
                        </td>
                        <td style={{ padding: '12px', color: '#4a5568', fontSize: '13px' }}>
                          {asset.location?.site?.name || 'N/A'}
                        </td>
                        <td style={{ padding: '12px', color: '#4a5568', fontSize: '13px' }}>
                          {asset.location?.locationDetail || 'N/A'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {availableAssets.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalAssets}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(newItemsPerPage) => {
                    setItemsPerPage(newItemsPerPage);
                    setCurrentPage(1);
                  }}
                  showItemsPerPage={true}
                />
              </div>
            )}

            {/* Add Selected Button */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end',
              marginTop: '16px',
              paddingTop: '16px',
              borderTop: '1px solid #e2e8f0'
            }}>
              <button
                type="button"
                onClick={() => navigate('/admin/checkin/form', { state: { selectedAssets } })}
                disabled={selectedAssets.length === 0}
                style={{
                  padding: '10px 20px',
                  background: selectedAssets.length === 0 
                    ? '#cbd5e0' 
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: selectedAssets.length === 0 ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: selectedAssets.length === 0 ? 0.6 : 1,
                  transition: 'all 0.2s'
                }}
              >
                <span>+</span>
                <span>Add Selected ({selectedAssets.length})</span>
              </button>
            </div>
          </>
        )}
      </div>

    </div>
  );
};

export default CheckinPage;
